// Rewrite Supabase Storage object URLs to use the on-the-fly image transform
// endpoint so we ship appropriately-sized/compressed variants instead of the
// full-res originals HubSpot flagged as USE_CORRECTLY_SIZED_IMAGES /
// USE_SUFFICIENTLY_COMPRESSED_IMAGES. Non-Supabase URLs (Cloudinary, static
// assets, external hosts) pass through unchanged.

const SUPABASE_OBJECT_PATH = "/storage/v1/object/public/";
const SUPABASE_RENDER_PATH = "/storage/v1/render/image/public/";

export interface TransformOptions {
  width?: number;
  height?: number;
  quality?: number; // 20–100, defaults to 75
  resize?: "cover" | "contain" | "fill";
}

function isSupabaseObjectUrl(src: string): boolean {
  return src.includes(SUPABASE_OBJECT_PATH);
}

export function supabaseImageUrl(src: string, opts: TransformOptions = {}): string {
  if (!src || !isSupabaseObjectUrl(src)) return src;
  // Strip any existing query so callers can safely re-transform.
  const [base] = src.split("?");
  const rendered = base.replace(SUPABASE_OBJECT_PATH, SUPABASE_RENDER_PATH);
  const params = new URLSearchParams();
  if (opts.width) params.set("width", String(Math.round(opts.width)));
  if (opts.height) params.set("height", String(Math.round(opts.height)));
  params.set("quality", String(opts.quality ?? 75));
  if (opts.resize) params.set("resize", opts.resize);
  return `${rendered}?${params.toString()}`;
}

// Build a 1x/2x srcSet capped at 1920 for retina without doubling payload on
// standard-DPR displays.
export function supabaseSrcSet(src: string, displayWidth: number, quality = 75): string | undefined {
  if (!isSupabaseObjectUrl(src)) return undefined;
  const oneX = Math.min(1920, Math.round(displayWidth));
  const twoX = Math.min(1920, Math.round(displayWidth * 2));
  if (twoX <= oneX) return `${supabaseImageUrl(src, { width: oneX, quality })} 1x`;
  return [
    `${supabaseImageUrl(src, { width: oneX, quality })} 1x`,
    `${supabaseImageUrl(src, { width: twoX, quality })} 2x`,
  ].join(", ");
}

// Width-descriptor srcSet for fluid/responsive images. Pair with a `sizes`
// attribute so the browser picks the smallest adequate variant. Supabase's
// render endpoint content-negotiates modern formats (AVIF/WebP) from the
// browser's Accept header, so no per-format markup is needed.
export function supabaseSrcSetWidths(
  src: string,
  widths: number[] = [400, 640, 900, 1200, 1600],
  quality = 75,
): string | undefined {
  if (!src.includes(SUPABASE_OBJECT_PATH)) return undefined;
  return widths
    .map((w) => `${supabaseImageUrl(src, { width: w, quality })} ${w}w`)
    .join(", ");
}